import { useEffect, useState } from "react";
import { RefreshCw, Sparkles } from "lucide-react";
import FlipCard from "../components/FlipCard";
import { Api } from "../lib/api";

function datesRange(evs) {
  return evs.map((e) => `${e.month_short} ${e.day}`).join(" – ");
}

function ProgrammeCard({ events, concept }) {
  const first = events[0];
  if (!first) return null;
  const highlights = concept?.highlights || [];

  const front = (
    <div
      className="h-full w-full p-7 flex flex-col"
      style={{ backgroundColor: first.color }}
    >
      <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-cream/80">
        {datesRange(events)}
      </p>
      <h3 className="font-serif text-3xl md:text-4xl text-cream mt-2">
        {first.concept_name}
      </h3>
      <p className="text-cream/85 text-sm mt-3 leading-relaxed line-clamp-3">
        {concept?.blurb || first.description}
      </p>
      <div className="mt-auto flex flex-wrap gap-2">
        {events.map((e) => (
          <span
            key={e.id}
            className="text-[11px] font-bold uppercase tracking-wide rounded-round bg-cream/15 text-cream px-3 py-1"
          >
            {e.weekday} · {e.month_short} {e.day} · {e.time_range}
          </span>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-1 text-cream/85 text-xs">
        <RefreshCw size={12} /> Tap card to see what to expect
      </div>
    </div>
  );

  const back = (
    <div
      className="h-full w-full p-7 flex flex-col bg-cream text-navy"
      style={{ borderTop: `8px solid ${first.color}` }}
    >
      <p
        className="text-[10px] font-bold tracking-[0.18em] uppercase"
        style={{ color: first.color }}
      >
        What to expect
      </p>
      <h3 className="font-serif text-2xl md:text-3xl text-navy mt-1.5">
        {first.concept_name}
      </h3>
      <ul className="mt-4 space-y-2.5 flex-1">
        {highlights.map((h) => (
          <li
            key={h}
            className="flex items-start gap-2.5 text-sm leading-snug text-navy"
          >
            <Sparkles
              size={14}
              className="mt-0.5 shrink-0"
              style={{ color: first.color }}
            />
            <span>{h}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 pt-3 border-t border-[#EFE7D2] text-xs text-grey flex items-center gap-1">
        <RefreshCw size={11} /> Tap to flip back
      </div>
    </div>
  );

  return (
    <FlipCard
      className="h-[440px] md:h-[470px] rounded-2xl shadow-soft"
      front={front}
      back={back}
    />
  );
}

export default function ProgrammePage() {
  const [events, setEvents] = useState([]);
  const [conceptsByCid, setConceptsByCid] = useState({});

  useEffect(() => {
    Api.events().then(setEvents).catch(() => {});
    Api.concepts()
      .then((rows) =>
        setConceptsByCid(Object.fromEntries(rows.map((r) => [r.concept_id, r])))
      )
      .catch(() => {});
  }, []);

  const byConcept = new Map();
  for (const e of events) {
    if (!byConcept.has(e.concept_id)) byConcept.set(e.concept_id, []);
    byConcept.get(e.concept_id).push(e);
  }
  const concepts = Array.from(byConcept.entries());

  return (
    <div className="container-x py-12 md:py-20" data-testid="programme-page">
      <p className="eyebrow">August 19–20 · 2026 · Grinda</p>
      <h1 className="heading-xl mt-2">Two signature days.</h1>
      <h2 className="font-serif italic text-coral text-4xl md:text-6xl leading-tight">
        Discovery &amp; thanksgiving.
      </h2>
      <p className="body-lead mt-3 max-w-2xl">
        Picnic in the Park · The Genesis — two days on Grinda celebrating a
        great summer past. Tap a card to see what each day brings.
      </p>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
        {concepts.map(([cid, evs]) => (
          <ProgrammeCard key={cid} events={evs} concept={conceptsByCid[cid]} />
        ))}
      </div>
    </div>
  );
}
